import { useCallback, useRef, useState, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import UserReviewTile from './UserReviewTile'
import LineThumbsUpIcon from 'LuxKit/Icons/line/LineThumbsUpIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import RatingDistribution from 'components/Common/RatingDistribution'
import RatingBreakdownItem from 'components/Common/RatingBreakdownItem'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import useUserReviews from 'hooks/Reviews/useUserReviews'
import UserReviewPagination from './UserReviewPagination'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import type { ReviewCompanionshipType } from 'api/review'
import {
  mapRatingDistribution,
  mapRatingBreakdown,
  getRecommendPercentageText,
} from 'lib/reviews/ratingBreakdownUtils'
import ReviewsModal from './ReviewsModal'
import ReviewFilterBar from './ReviewFilterBar'
import type { ReviewPanelSortType } from 'constants/review'
import {
  RATING_RANGE_TO_FILTER_MAP,
} from 'constants/review'
import { OfferPageSnapshotContributionRegistration } from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantSurfaceRegistry'
import { buildOfferPageReviewsContribution } from 'tripPlanner/assistant/surfaces/offerPage/offerPageReviewsContribution'
import {
  getOfferPropertyReviewContext,
  getPageReviewSourceCapabilities,
  getReviewSourceCapabilities,
  type ReviewContext,
} from './reviewContext'
import {
  REVIEW_SECTION_CONTAINER_NAME,
  REVIEW_SECTION_WIDE_BREAKPOINT,
} from './reviewLayout'

const GraphSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${rem(20)};
`

const RatingBreakdownSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: ${rem(24)};
  column-gap: ${rem(32)};
  padding: ${rem(16)} ${rem(64)};
`

const RatingBreakdownSectionMobile = styled(Group)`
  padding: 0 ${rem(8)};
`

const MobileGraphSection = styled(VerticalSpacer)`
  padding: ${rem(24)} 0;
`

const RatingDistributionTable = styled(VerticalSpacer)`
  display: grid;
  grid-template-columns: max-content max-content max-content max-content;
  column-gap: ${rem(8)};
  align-items: center;
  width: max-content;
`

const SearchResultInfo = styled.div`
  padding: ${rem(12)} 0;
`

const EmptyState = styled.div`
  padding: ${rem(40)} 0;
  text-align: center;
`

const ReviewSectionRoot = styled(Group)`
  container: ${REVIEW_SECTION_CONTAINER_NAME} / inline-size;
`

const EmbeddedGraphSection = styled.div`
  display: grid;
  gap: ${rem(24)};

  @container ${REVIEW_SECTION_CONTAINER_NAME} (min-width: ${rem(REVIEW_SECTION_WIDE_BREAKPOINT)}) {
    grid-template-columns: minmax(${rem(180)}, 0.8fr) minmax(${rem(300)}, 1.2fr) minmax(${rem(220)}, 1fr);
    align-items: start;
  }
`

const EmbeddedRatingBreakdownSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${rem(24)};
`

const EmbeddedHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  @container ${REVIEW_SECTION_CONTAINER_NAME} (min-width: ${rem(480)}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

const PAGE_SIZE = 4

interface Props {
  property?: App.OfferProperty | App.BedbankProperty
  isLPP?: boolean
  experience?: App.ExperienceOffer
  tour?: App.Tours.TourV2Offer
  reviewContext?: ReviewContext
  presentation?: 'page' | 'embedded'
  showReviewFiltersAndUserDetails?: boolean
  reviewsThreshold?: number
}

const DEFAULT_SORT: ReviewPanelSortType = 'recommend'

function UserReviewSection({
  property,
  isLPP,
  experience,
  tour,
  reviewContext,
  presentation = 'page',
  showReviewFiltersAndUserDetails = true,
  reviewsThreshold,
}: Props) {
  const intl = useIntl()
  const legacyReviewContext = useMemo<ReviewContext | undefined>(() => {
    if (property) {
      return getOfferPropertyReviewContext(property, isLPP ? 'bedbank' : 'luxuryescapes')
    }

    if (experience?.rating) {
      return {
        target: { kind: 'experience', experienceId: experience.id },
        rating: experience.rating,
        entityType: 'experience',
      }
    }

    if (tour?.rating) {
      return {
        target: { kind: 'tour', tourId: tour.id },
        rating: tour.rating,
        entityType: 'tour',
      }
    }
  }, [experience, isLPP, property, tour])
  const resolvedReviewContext = reviewContext ?? legacyReviewContext
  const isEmbedded = presentation === 'embedded'
  const reviewTarget = resolvedReviewContext?.target
  const rootRef = useRef<HTMLDivElement>(null)
  // As the user changes pages, we want to show the old reviews until it's done loading
  // so we'll store them here when this happens so we can display them
  const [isReviewsModalOpen, setIsReviewsModalOpen] = useState(false)
  const [initialRatingFilter, setInitialRatingFilter] = useState<string>('')
  const [initialReviewId, setInitialReviewId] = useState<string>('')
  const [localPage, setLocalPage] = useState<number>(1)
  const [oldReviews, setOldReviews] = useState<Array<App.Review>>([])
  const [searchInput, setSearchInput] = useState<string>('')
  const [activeSearch, setActiveSearch] = useState<string>('')

  // Filter state
  const [sort, setSort] = useState<ReviewPanelSortType>(DEFAULT_SORT)
  const [ratingFilter, setRatingFilter] = useState<string>('')
  const [travellerFilter, setTravellerFilter] = useState<ReviewCompanionshipType | ''>('')

  const onOpenReviewsModal = useCallback(() => {
    setInitialRatingFilter('')
    setInitialReviewId('')
    setIsReviewsModalOpen(true)
  }, [])

  const onOpenReviewsModalWithReview = useCallback((reviewId: string) => {
    setInitialRatingFilter('')
    setInitialReviewId(reviewId)
    setIsReviewsModalOpen(true)
  }, [])

  const onOpenReviewsModalWithFilter = useCallback((ratingRange: string) => {
    const filter = RATING_RANGE_TO_FILTER_MAP[ratingRange] || ''
    setInitialRatingFilter(filter)
    setIsReviewsModalOpen(true)
  }, [])

  const onCloseReviewsModal = useCallback(() => {
    setIsReviewsModalOpen(false)
    setInitialRatingFilter('')
    setInitialReviewId('')
  }, [])

  // Search handlers
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
  }, [])

  const handleSearchSubmit = useCallback(() => {
    setActiveSearch(searchInput)
    setLocalPage(1)
    setOldReviews([])
  }, [searchInput])

  // Sort handler — reset pagination inline to avoid extra render from useEffect
  const handleSortChange = useCallback((value: ReviewPanelSortType) => {
    setSort(value)
    setLocalPage(1)
    setOldReviews([])
  }, [])

  // Rating filter handler
  const handleRatingChange = useCallback((value: string) => {
    setRatingFilter(value)
    setLocalPage(1)
    setOldReviews([])
  }, [])

  // Traveller filter handler
  const handleTravellerChange = useCallback((value: ReviewCompanionshipType | '') => {
    setTravellerFilter(value)
    setLocalPage(1)
    setOldReviews([])
  }, [])

  const handleRatingDistributionClick = useCallback((ratingRange: string) => {
    if (isEmbedded) {
      const filter = RATING_RANGE_TO_FILTER_MAP[ratingRange] || ''
      handleRatingChange(ratingFilter === filter ? '' : filter)
      return
    }

    onOpenReviewsModalWithFilter(ratingRange)
  }, [handleRatingChange, isEmbedded, onOpenReviewsModalWithFilter, ratingFilter])

  const offset = (localPage - 1) * PAGE_SIZE
  const [reviews, fetchingReviews] = useUserReviews({
    propertyProvider: reviewTarget?.kind === 'property' ? reviewTarget.propertyProvider : undefined,
    propertyId: reviewTarget?.kind === 'property' ? reviewTarget.propertyId : undefined,
    experienceId: reviewTarget?.kind === 'experience' ? reviewTarget.experienceId : undefined,
    tourId: reviewTarget?.kind === 'tour' ? reviewTarget.tourId : undefined,
    useBedbankReviews: true,
    useExternalReviews: true,
    useHiddenForProperty: true,
    includeTranslations: true,
    limit: PAGE_SIZE,
    offset,
    sort,
    ratings: ratingFilter ? [ratingFilter] : undefined,
    companionship: travellerFilter ? [travellerFilter] : undefined,
    search: activeSearch || undefined,
  })

  const displayableRating = resolvedReviewContext?.rating
  const reviewSourceCapabilities = isEmbedded ?
      getReviewSourceCapabilities(displayableRating?.reviewsSource) :
      getPageReviewSourceCapabilities(displayableRating?.reviewsSource)
  const isGoogleReviews = reviewSourceCapabilities.attribution === 'google'
  const isLuxuryEscapesReviews = reviewSourceCapabilities.verifiedByLuxuryEscapes

  const reviewsFromHook = reviews?.reviews
  const visibleReviews = useMemo(
    () => fetchingReviews ? oldReviews : reviewsFromHook ?? [],
    [fetchingReviews, oldReviews, reviewsFromHook],
  )
  const visibleReviewsRef = useRef(visibleReviews)
  visibleReviewsRef.current = visibleReviews

  const enrichedReviews = useMemo(() => visibleReviews.map(review => ({
    ...review,
    experienceId: reviewTarget?.kind === 'experience' ? reviewTarget.experienceId : undefined,
    tourId: reviewTarget?.kind === 'tour' ? reviewTarget.tourId : undefined,
  })), [reviewTarget, visibleReviews])

  const onPageChange = useCallback((page: number) => {
    setLocalPage(page)
    setOldReviews(visibleReviewsRef.current)
    rootRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])
  const offerRatingDisplayable = isOfferRatingDisplayable(displayableRating, reviewsThreshold)
  // The rating summary atom is contributed eagerly from `buildOfferPageSnapshot`'s own
  // `reviewsSummarySnapshot` instead (sourced from the same always-visible header badge data),
  // since this section -- and the individual `review` atoms below -- only mount once scrolled into
  // view, which is too late for a fact tagged `modelUse: 'manifest'` to reliably exist yet.
  const reviewsContribution = useMemo(() => buildOfferPageReviewsContribution(
    'inline',
    visibleReviews,
  ), [visibleReviews])

  // Determine if "View all reviews" CTA should be shown
  // Hide for Google reviews as they cannot be viewed in our platform
  const displayReviewsNumber = offerRatingDisplayable && !isGoogleReviews
  const showViewAllReviews = reviewSourceCapabilities.supportsViewAll

  // Extract rating breakdown data from property, experience, or tour
  // Note: Currently only properties have reviewsRatingBreakdown in the backend
  const ratingBreakdown = displayableRating?.reviewsRatingBreakdown

  // Map rating distribution from backend format to frontend format
  const ratingDistributions = useMemo(
    () => mapRatingDistribution(ratingBreakdown?.ratingDistribution, intl),
    [ratingBreakdown?.ratingDistribution, intl],
  )

  // Map rating breakdown items from backend format to frontend format
  const ratingBreakdowns = useMemo(
    () => mapRatingBreakdown(ratingBreakdown, intl),
    [ratingBreakdown, intl],
  )

  // Get recommend percentage text
  const entityType = resolvedReviewContext?.entityType ?? 'experience'
  const recommendText = useMemo(
    () => getRecommendPercentageText(ratingBreakdown?.recommendPercentage, entityType, intl),
    [ratingBreakdown?.recommendPercentage, entityType, intl],
  )

  // Translated strings
  const reviewsFromGoogleSubtitle = intl.formatMessage({
    id: 'BwsW6IPCoTz2oPVsFO7-',
    defaultMessage: 'Reviews from Google',
    description: 'Subtitle shown when reviews are sourced from Google',
  })

  const verifiedGuestsHeading = intl.formatMessage({
    id: 'xUq2Aop57PZud70LjEZG',
    defaultMessage: 'Reviews from verified guests',
    description: 'Heading for the customer reviews section in the reviews section',
  })

  const guestReviewsHeading = intl.formatMessage({
    id: 't4BT-ihOUb1v97iKEQv9',
    defaultMessage: 'Guest reviews',
    description: 'Neutral heading shown above reviews that are not verified Luxury Escapes guest reviews',
  })

  const reviewsSectionHeading = intl.formatMessage({
    id: 'Aon9y8geKIVv2j3E65Oe',
    defaultMessage: 'Reviews',
    description: 'Section heading shown above hotel reviews in the Trip Planner offer detail drawer',
  })

  const reviewListHeading = isLuxuryEscapesReviews ? verifiedGuestsHeading : guestReviewsHeading

  const viewAllCountLabel = intl.formatMessage(
    {
      id: 'aBsHJY_YhJF8dXewFI0j',
      defaultMessage: 'View all {count, plural, one {# review} other {# reviews}}',
      description: 'Button text to open reviews panel showing total review count',
    },
    { count: displayableRating?.reviewsTotal },
  )
  const viewAllLabel = intl.formatMessage({
    id: '5v7CEGr2rIr7Fl5IHqLX',
    defaultMessage: 'View all reviews',
    description: 'Button text to open reviews panel without showing count',
  })
  const viewAllReviewsLabel = displayReviewsNumber ? viewAllCountLabel : viewAllLabel

  const noFilterResultsText = intl.formatMessage({
    id: '4tG4kfAhcxdLsPUUaBb9',
    defaultMessage: 'No reviews found with the selected filters',
    description: 'Empty state message when review filters return no results',
  })

  const reviewsPaginationLabel = intl.formatMessage({
    id: 'IWseQkGj7w4-3kjm1EES',
    defaultMessage: 'Reviews',
    description: 'Accessible label for the reviews pagination section',
  })

  return (
    <ReviewSectionRoot
      direction="vertical"
      gap={24}
      ref={rootRef}
    >
      {!isEmbedded && <OfferPageSnapshotContributionRegistration contribution={reviewsContribution} />}
      <LoadingIndicator floating visible={fetchingReviews} />
      {isEmbedded && <Heading variant="heading3">{reviewsSectionHeading}</Heading>}
      {isEmbedded && reviewSourceCapabilities.supportsDetailedSummary && (
        <EmbeddedGraphSection>
          <VerticalSpacer gap={24}>
            {offerRatingDisplayable && <NumberRating
              variant="xl"
              rating={displayableRating.score}
              total={displayableRating.reviewsTotal}
            />}
            {recommendText && (
              <BodyTextBlock
                weight="bold"
                variant="medium"
                startIcon={<LineThumbsUpIcon colour="highlight-primary-dark" />}
              >
                {recommendText}
              </BodyTextBlock>
            )}
          </VerticalSpacer>
          {ratingDistributions.length > 0 && (
            <RatingDistributionTable gap={8}>
              {ratingDistributions.map(item => (
                <RatingDistribution
                  key={item.id}
                  label={item.label}
                  range={item.range}
                  progress={item.progress}
                  count={item.count}
                  textVariant="small"
                  onClick={() => handleRatingDistributionClick(item.range)}
                  selected={ratingFilter === (RATING_RANGE_TO_FILTER_MAP[item.range] || '')}
                />
              ))}
            </RatingDistributionTable>
          )}
          {ratingBreakdowns.length > 0 && (
            <EmbeddedRatingBreakdownSection>
              {ratingBreakdowns.map(item => (
                <RatingBreakdownItem
                  key={item.id}
                  score={item.score}
                  label={item.label}
                />
              ))}
            </EmbeddedRatingBreakdownSection>
          )}
        </EmbeddedGraphSection>
      )}
      {isEmbedded && !reviewSourceCapabilities.supportsDetailedSummary && offerRatingDisplayable && (
        <NumberRating
          variant="xl"
          rating={displayableRating.score}
          total={displayableRating.reviewsTotal}
          ratingSubtitle={isGoogleReviews ? reviewsFromGoogleSubtitle : undefined}
        />
      )}
      {isEmbedded && <EmbeddedHeader>
        <Heading variant="heading4">{reviewListHeading}</Heading>
        {showViewAllReviews && <TextButton kind="secondary" onClick={onOpenReviewsModal}>{viewAllReviewsLabel}</TextButton>}
      </EmbeddedHeader>}
      {/* Graph Section - Desktop */}
      {!isEmbedded && reviewSourceCapabilities.supportsDetailedSummary && <><CSSBreakpoint min="tablet">
        <GraphSection>
          {/* Review Section */}
          <VerticalSpacer gap={32}>
            {offerRatingDisplayable && <NumberRating
              variant="xxl"
              rating={displayableRating.score}
              total={displayableRating.reviewsTotal}
              ratingSubtitle={isGoogleReviews ? reviewsFromGoogleSubtitle : undefined}
              onReviewSubTitleClick={onOpenReviewsModal}
            />}
            {recommendText && (
              <BodyTextBlock
                weight="bold"
                variant="medium"
                startIcon={<LineThumbsUpIcon colour="highlight-primary-dark" />}
              >
                {recommendText}
              </BodyTextBlock>
            )}
          </VerticalSpacer>
          {/* Rating Distribution */}
          {ratingDistributions.length > 0 && (
            <RatingDistributionTable gap={0}>
              {ratingDistributions.map(item => (
                <RatingDistribution
                  key={item.id}
                  label={item.label}
                  range={item.range}
                  progress={item.progress}
                  count={item.count}
                  onClick={() => handleRatingDistributionClick(item.range)}
                />
              ))}
            </RatingDistributionTable>
          )}
          {/* Rating Breakdown */}
          {ratingBreakdowns.length > 0 && (
            <RatingBreakdownSection>
              {ratingBreakdowns.map(item => (
                <RatingBreakdownItem
                  key={item.id}
                  score={item.score}
                  label={item.label}
                />
              ))}
            </RatingBreakdownSection>
          )}
        </GraphSection>
      </CSSBreakpoint>
      {/* Graph Section - Mobile */}
      <CSSBreakpoint max="mobile">
        <MobileGraphSection gap={24}>
          {/* Review Section */}
          {offerRatingDisplayable && <NumberRating
            variant="xl"
            rating={displayableRating.score}
            total={displayableRating.reviewsTotal}
            ratingSubtitle={isGoogleReviews ? reviewsFromGoogleSubtitle : undefined}
            onReviewSubTitleClick={onOpenReviewsModal}
          />}
          {/* Rating Distribution */}
          {ratingDistributions.length > 0 && (
            <RatingDistributionTable gap={8}>
              {ratingDistributions.map(item => (
                <RatingDistribution
                  key={item.id}
                  label={item.label}
                  range={item.range}
                  progress={item.progress}
                  count={item.count}
                  textVariant="small"
                  onClick={() => handleRatingDistributionClick(item.range)}
                />
              ))}
            </RatingDistributionTable>
          )}
          {/* Rating Breakdown */}
          {ratingBreakdowns.length > 0 && (
            <RatingBreakdownSectionMobile direction="horizontal" gap={0} horizontalAlign="space-between">
              {ratingBreakdowns.map(item => (
                <RatingBreakdownItem
                  key={item.id}
                  score={item.score}
                  label={item.label}
                />
              ))}
            </RatingBreakdownSectionMobile>
          )}
          {recommendText && (
            <BodyTextBlock
              weight="bold"
              variant="medium"
              startIcon={<LineThumbsUpIcon colour="highlight-primary-dark" />}
            >
              {recommendText}
            </BodyTextBlock>
          )}
        </MobileGraphSection>
      </CSSBreakpoint>
      </>}
      {/* Reviews Header - Desktop */}
      {!isEmbedded && <CSSBreakpoint min="desktop">
        <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
          {offerRatingDisplayable && isGoogleReviews && <NumberRating
            variant="xxl"
            rating={displayableRating.score}
            total={displayableRating.reviewsTotal}
            ratingSubtitle={isGoogleReviews ? reviewsFromGoogleSubtitle : undefined}
          />}
          {!isGoogleReviews && <Heading variant="heading4">{verifiedGuestsHeading}</Heading>}
          {showViewAllReviews && <TextButton kind="secondary" onClick={onOpenReviewsModal}>{viewAllReviewsLabel}</TextButton>}
        </Group>
      </CSSBreakpoint>}
      {/* Reviews Header - Tablet */}
      {!isEmbedded && <CSSBreakpoint max="tablet">
        {isGoogleReviews && offerRatingDisplayable && <NumberRating
          variant="xl"
          rating={displayableRating.score}
          total={displayableRating.reviewsTotal}
          ratingSubtitle={isGoogleReviews ? reviewsFromGoogleSubtitle : undefined}
        />}
        {!isGoogleReviews && <Heading variant="heading4">{verifiedGuestsHeading}</Heading>}
      </CSSBreakpoint>}
      {/* Search Input & Filter Chips */}
      {reviewSourceCapabilities.supportsFilters && (
        <ReviewFilterBar
          searchInput={searchInput}
          onSearchChange={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
          sort={sort}
          defaultSort={DEFAULT_SORT}
          onSortChange={handleSortChange}
          ratingFilter={ratingFilter}
          onRatingChange={handleRatingChange}
          travellerFilter={travellerFilter}
          onTravellerChange={handleTravellerChange}
          portaledDropdowns={isEmbedded}
        />
      )}
      {/* Search Result Info */}
      {activeSearch && visibleReviews.length > 0 && (
        <SearchResultInfo>
          <BodyText variant="medium">
            <FormattedMessage
              id="u2KdH-wUyDXr6jOTxKwp"
              defaultMessage="{reviewCountLabel} mentioned ''<b>{searchTerm}</b>''"
              description="Review search summary showing the number of matching reviews and the user-entered search term"
              values={{
                reviewCountLabel: intl.formatMessage(
                  {
                    id: 'lLDfRBm2kUtJjogJtKQf',
                    defaultMessage: '{count, plural, one {# review} other {# reviews}}',
                    description: 'Review count label shown in user review search results',
                  },
                  { count: reviews?.total ?? visibleReviews.length },
                ),
                searchTerm: activeSearch,
                b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              }}
            />
          </BodyText>
        </SearchResultInfo>
      )}
      {activeSearch && visibleReviews.length === 0 && !fetchingReviews && (
        <EmptyState>
          <BodyText variant="medium">
            <FormattedMessage
              id="1-ox8un5C1nlh0o7af1a"
              defaultMessage="There are no results for ''<b>{searchTerm}</b>''"
              description="Empty state message when review search returns no results"
              values={{
                searchTerm: activeSearch,
                b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              }}
            />
          </BodyText>
        </EmptyState>
      )}
      {!activeSearch && (ratingFilter || travellerFilter) && visibleReviews.length === 0 && !fetchingReviews && (
        <EmptyState>
          <BodyText variant="medium">
            {noFilterResultsText}
          </BodyText>
        </EmptyState>
      )}
      {/* Reviews List with Pagination */}
      {!!visibleReviews.length && <UserReviewPagination
        label={reviewsPaginationLabel}
        total={reviews?.total ?? 0}
        pageSize={PAGE_SIZE}
        currentPage={localPage}
        onPageChange={onPageChange}
      >
        <Group direction="vertical" gap={24}>
          {enrichedReviews.map(review => <UserReviewTile
            key={review.id}
            userReview={review}
            variant={isEmbedded ? 'embedded' : 'default'}
            showReviewFiltersAndUserDetails={showReviewFiltersAndUserDetails}
            onOpenReviewsModal={onOpenReviewsModalWithReview}
            searchTerm={activeSearch}
          />)}
        </Group>
      </UserReviewPagination>}
      {/* View All - Tablet */}
      {showViewAllReviews && !isEmbedded && <CSSBreakpoint max="tablet">
        <Group direction="horizontal" horizontalAlign="center">
          <TextButton kind="secondary" onClick={onOpenReviewsModal}>{viewAllReviewsLabel}</TextButton>
        </Group>
      </CSSBreakpoint>}

      {resolvedReviewContext && <ReviewsModal
        isOpen={isReviewsModalOpen}
        onClose={onCloseReviewsModal}
        reviewContext={resolvedReviewContext}
        initialRatingFilter={initialRatingFilter}
        initialReviewId={initialReviewId}
        initialSort={DEFAULT_SORT}
      />}
    </ReviewSectionRoot>
  )
}

export default React.memo(UserReviewSection)
